import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { fetchTypesForBrand, fetchBrandById } from "../api/BrandAPIs";
import { createType, updateType, deleteType } from "../api/TypeAPIs";
import { colors } from "../constants/colors";

const SkeletonLoader = ({ type }) => {
  if (type === "table") {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded mb-4"></div>
        {[...Array(3)].map((_, index) => (
          <div key={index} className="flex gap-4 border-b py-3">
            <div className="w-12 h-12 bg-gray-200 rounded"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const TypesPage = () => {
  const { brandId } = useParams();
  const navigate = useNavigate();
  const [types, setTypes] = useState([]);
  const [brandData, setBrandData] = useState(null);
  const [newType, setNewType] = useState(null);
  const [editingType, setEditingType] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Load brand and types in parallel
        const [brandResponse, typesResponse] = await Promise.all([
          fetchBrandById(brandId),
          fetchTypesForBrand(brandId)
        ]);

        // Set brand data
        setBrandData(brandResponse);

        // Set types data
        if (typesResponse && typesResponse.length > 0) {
          const processedTypes = typesResponse
            .map((type) => ({
              id: type.typeId,
              name: type.name,
              sales: type.sales || 0,
              isAvailable: type.isAvailable !== undefined ? type.isAvailable : true,
              brandId: parseInt(brandId) // Include brandId for updates
            }))
            .sort((a, b) => b.sales - a.sales);
          setTypes(processedTypes);
        } else {
          setTypes([]);
        }
      } catch (err) {
        console.error("Error loading data:", err);
        setError(err.message || "فشل في تحميل البيانات");
      } finally {
        setIsLoading(false);
      }
    };

    if (brandId) {
      loadData();
    }
  }, [brandId]);

  const handleInputChange = (e, setState, state) => {
    const { name, value, type: inputType, checked } = e.target;
    setState((prevState) => ({
      ...prevState,
      [name]: inputType === "checkbox" ? checked : value,
    }));
  };

  const handleCreateType = async (e) => {
    e.preventDefault();
    try {
      const createPayload = {
        ...newType,
        brandId: parseInt(brandId)
      };
      const created = await createType(createPayload);
      
      // Immediately add to local state
      const newTypeItem = {
        id: created.typeId,
        name: created.name,
        sales: created.sales || 0,
        isAvailable: created.isAvailable !== undefined ? created.isAvailable : true,
        brandId: parseInt(brandId)
      };
      
      setTypes(prevTypes => 
        [...prevTypes, newTypeItem].sort((a, b) => b.sales - a.sales)
      );
      setNewType(null);
    } catch (err) {
      console.error("Error creating type:", err);
      alert("فشل في إنشاء النوع: " + err.message);
    }
  };

  const handleUpdateType = async (e) => {
    e.preventDefault();
    try {
      const updatePayload = {
        ...editingType,
        brandId: parseInt(brandId)
      };
      await updateType(editingType.id, updatePayload);
      
      // Immediately update local state
      setTypes(prevTypes =>
        prevTypes.map(type =>
          type.id === editingType.id
            ? {
                ...type,
                name: editingType.name,
                isAvailable: editingType.isAvailable,
                brandId: parseInt(brandId)
              }
            : type
        ).sort((a, b) => b.sales - a.sales)
      );
      setEditingType(null);
    } catch (err) {
      console.error("Error updating type:", err);
      alert("فشل في تحديث النوع: " + err.message);
    }
  };

  const handleToggleAvailability = async (type) => {
    try {
      const updatedTypePayload = { 
        ...type, 
        isAvailable: !type.isAvailable,
        brandId: parseInt(brandId)
      };
      await updateType(type.id, updatedTypePayload);
      
      // Immediately update local state
      setTypes(prevTypes =>
        prevTypes.map(t => 
          t.id === type.id
            ? { ...t, isAvailable: !t.isAvailable }
            : t
        )
      );
    } catch (err) {
      console.error("Error updating type availability:", err);
      alert("فشل في تحديث حالة التوافر: " + err.message);
    }
  };

  const handleDeleteType = async (id) => {
    if (window.confirm("هل أنت متأكد أنك تريد حذف هذا النوع؟")) {
      try {
        await deleteType(id);
        // Immediately remove from local state
        setTypes(prevTypes => prevTypes.filter((type) => type.id !== id));
      } catch (err) {
        console.error("Error deleting type:", err);
        alert("فشل في حذف النوع: " + err.message);
      }
    }
  };

  // Refresh data function (optional - for manual refresh)
  const refreshData = async () => {
    try {
      setIsLoading(true);
      const typesResponse = await fetchTypesForBrand(brandId);
      
      if (typesResponse && typesResponse.length > 0) {
        const processedTypes = typesResponse
          .map((type) => ({
            id: type.typeId,
            name: type.name,
            sales: type.sales || 0,
            isAvailable: type.isAvailable !== undefined ? type.isAvailable : true,
            brandId: parseInt(brandId)
          }))
          .sort((a, b) => b.sales - a.sales);
        setTypes(processedTypes);
      } else {
        setTypes([]);
      }
    } catch (err) {
      console.error("Error refreshing data:", err);
      setError(err.message || "فشل في تحديث البيانات");
    } finally {
      setIsLoading(false);
    }
  };

  if (!brandId) {
    return (
      <div className="min-h-screen bg-gray-50 p-4" dir="rtl">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <h1 className="text-2xl font-bold text-red-600">خطأ في التحميل</h1>
            <p className="mt-2 text-gray-600">معرف العلامة التجارية مفقود</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4" dir="rtl">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl md:text-3xl font-bold">
            أنواع {brandData ? brandData.name : "جاري التحميل..."}
          </h1>
          <div className="flex items-center gap-2">
            <button
              onClick={refreshData}
              className="text-sm text-blue-600 hover:text-blue-800"
              disabled={isLoading}
            >
              تحديث
            </button>
            <button
              onClick={() => navigate("/brands")}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-50"
              disabled={isLoading}
            >
              العودة إلى العلامات التجارية
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-100 text-red-700 p-4 rounded mb-6 border border-red-200">
            <div className="flex justify-between items-center">
              <span>{error}</span>
              <button
                onClick={() => refreshData()}
                className="text-red-600 hover:text-red-800 text-sm"
              >
                إعادة المحاولة
              </button>
            </div>
          </div>
        )}

        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl md:text-2xl font-semibold">
            الأنواع ({types.length})
          </h2>
          <button
            onClick={() => setNewType({ name: "", isAvailable: true })}
            className="bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600 disabled:opacity-50"
            style={{ backgroundColor: colors.orange }}
            disabled={isLoading}
          >
            إضافة نوع جديد
          </button>
        </div>

        {isLoading ? (
          <SkeletonLoader type="table" />
        ) : types.length ? (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    الرقم
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    اسم النوع
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    المبيعات
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    التوافر
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    الإجراءات
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {types.map((type) => (
                  <tr 
                    key={type.id} 
                    className={`transition-colors ${
                      type.isAvailable ? "" : "bg-red-50 opacity-75"
                    }`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {type.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {type.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                      {type.sales}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                      <button
                        onClick={() => handleToggleAvailability(type)}
                        disabled={isLoading}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors disabled:opacity-50 ${
                          type.isAvailable
                            ? "bg-green-100 text-green-800 hover:bg-green-200"
                            : "bg-red-100 text-red-800 hover:bg-red-200"
                        }`}
                      >
                        {type.isAvailable ? "متاح" : "غير متاح"}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() =>
                            setEditingType({
                              id: type.id,
                              name: type.name,
                              sales: type.sales,
                              isAvailable: type.isAvailable,
                              brandId: type.brandId
                            })
                          }
                          disabled={isLoading}
                          className="bg-yellow-500 text-white px-3 py-1 rounded hover:bg-yellow-600 disabled:opacity-50"
                        >
                          تعديل
                        </button>
                        <button
                          onClick={() => handleDeleteType(type.id)}
                          disabled={isLoading}
                          className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 disabled:opacity-50"
                        >
                          حذف
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : !isLoading && (
          <div className="text-center py-12">
            <p className="text-gray-500">لا توجد أنواع</p>
          </div>
        )}

        {/* Create Modal */}
        {newType && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg w-full max-w-md">
              <h2 className="text-xl font-semibold mb-4" dir="rtl">
                إضافة نوع جديد
              </h2>
              <form onSubmit={handleCreateType} dir="rtl">
                <div className="mb-4">
                  <label className="block text-right mb-2">اسم النوع</label>
                  <input
                    type="text"
                    name="name"
                    value={newType.name}
                    onChange={(e) => handleInputChange(e, setNewType, newType)}
                    className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                    disabled={isLoading}
                  />
                </div>
                <div className="mb-4">
                  <label className="flex items-center text-right">
                    <input
                      type="checkbox"
                      name="isAvailable"
                      checked={newType.isAvailable}
                      onChange={(e) => handleInputChange(e, setNewType, newType)}
                      className="mr-2"
                      disabled={isLoading}
                    />
                    متاح للعرض
                  </label>
                </div>
                <div className="flex justify-end gap-4">
                  <button
                    type="button"
                    onClick={() => setNewType(null)}
                    className="bg-gray-300 text-black py-2 px-4 rounded hover:bg-gray-400 disabled:opacity-50"
                    disabled={isLoading}
                  >
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    className="bg-orange-500 text-white py-2 px-4 rounded hover:bg-orange-600 disabled:opacity-50"
                    style={{ backgroundColor: colors.orange }}
                    disabled={isLoading}
                  >
                    إنشاء
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {editingType && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg w-full max-w-md">
              <h2 className="text-xl font-semibold mb-4" dir="rtl">
                تعديل النوع #{editingType.id}
              </h2>
              <form onSubmit={handleUpdateType} dir="rtl">
                <div className="mb-4">
                  <label className="block text-right mb-2">اسم النوع</label>
                  <input
                    type="text"
                    name="name"
                    value={editingType.name}
                    onChange={(e) => handleInputChange(e, setEditingType, editingType)}
                    className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                    disabled={isLoading}
                  />
                </div>
                <div className="mb-4">
                  <label className="flex items-center text-right">
                    <input
                      type="checkbox"
                      name="isAvailable"
                      checked={editingType.isAvailable}
                      onChange={(e) => handleInputChange(e, setEditingType, editingType)}
                      className="mr-2"
                      disabled={isLoading}
                    />
                    متاح للعرض
                  </label>
                </div>
                <div className="flex justify-end gap-4">
                  <button
                    type="button"
                    onClick={() => setEditingType(null)}
                    className="bg-gray-300 text-black py-2 px-4 rounded hover:bg-gray-400 disabled:opacity-50"
                    disabled={isLoading}
                  >
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    className="bg-orange-500 text-white py-2 px-4 rounded hover:bg-orange-600 disabled:opacity-50"
                    style={{ backgroundColor: colors.orange }}
                    disabled={isLoading}
                  >
                    حفظ
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TypesPage;